import { zodResolver } from '@hookform/resolvers/zod';
import { Box, Checkbox, FormHelperText, Typography } from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ReminderHighlight } from 'src/components/atoms/reminder-highlight';
import { Dialog, DialogActions } from 'src/components/molecules/dialog';
import { useDeleteEmployee } from 'src/features/employees/api/hooks/use-delete-employee';
import { z } from 'zod';
import { EmployeesActionsSlotPayload } from '../employees-table-row';

interface Props extends DialogActions {
  selectedEmployee: EmployeesActionsSlotPayload | null;
}

export const DeleteEmployeeDialog = ({
  open,
  onCancel,
  selectedEmployee,
}: Props) => {
  const { t } = useTranslation();

  const { mutate: deleteEmployee, isPending: isDeletingEmployee } =
    useDeleteEmployee(onCancel);

  const handleDeleteEmployee = () => {
    if (selectedEmployee) {
      deleteEmployee(selectedEmployee.token);
    }
  };

  let content = (
    <Typography variant="body2">
      {t('employees:employees.deleteEmployeeDialog.message', {
        email: selectedEmployee?.email,
      })}
    </Typography>
  );

  // TODO: Fetch employee bookings
  const employeeHasActiveBookings = false;

  const formValidationSchema = z.object({
    confirm: z
      .boolean()
      .refine(value => (employeeHasActiveBookings ? value : true), {
        message: t('validation:required'),
      }),
  });

  type FormValues = z.infer<typeof formValidationSchema>;

  const defaultValues: FormValues = {
    confirm: false,
  };

  const { handleSubmit, control, reset } = useForm<FormValues>({
    resolver: zodResolver(formValidationSchema),
    mode: 'onBlur',
    defaultValues,
  });

  const handleOnCancel = () => {
    reset(defaultValues);
    onCancel?.();
  };

  if (employeeHasActiveBookings) {
    content = (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <ReminderHighlight>
          <Typography variant="body2">
            {t(
              'employees:employees.deleteEmployeeDialog.activeBookingsWarning',
            )}
          </Typography>
        </ReminderHighlight>

        <Typography variant="body2">
          {t('employees:employees.deleteEmployeeDialog.confirmationMessage')}
        </Typography>

        <Controller
          name="confirm"
          control={control}
          render={({
            field: { name, onChange, onBlur, value },
            fieldState: { error, invalid },
          }) => (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                <Checkbox
                  name={name}
                  onChange={onChange}
                  onBlur={onBlur}
                  checked={value}
                  color="primary"
                />

                <Typography variant="body2">
                  {t(
                    'employees:employees.deleteEmployeeDialog.checkboxMessage',
                  )}
                </Typography>
              </Box>

              <FormHelperText error={invalid}>{error?.message}</FormHelperText>
            </Box>
          )}
        />
      </Box>
    );
  }

  return (
    <Dialog
      variant="warning"
      open={open}
      title={t('employees:employees.deleteEmployeeDialog.title')}
      onCancel={handleOnCancel}
      onConfirm={handleSubmit(handleDeleteEmployee)}
      isConfirmButtonSubmitting={isDeletingEmployee}
      cancelButtonText={
        employeeHasActiveBookings
          ? t('common:button.no')
          : t('common:button.cancel')
      }
      confirmButtonId="delete_employee_confirm_button"
      confirmButtonText={
        employeeHasActiveBookings
          ? t('common:button.confirm')
          : t('employees:employees.deleteEmployeeDialog.buttonText')
      }>
      {content}
    </Dialog>
  );
};
